import { bool } from "prop-types"
import ExistingAttendeeListItem from "./existing_attendee_list_item"
import { meetsAttendeeTypeAgeRestriction } from "utils/age_restriction_utils"
import Icon from "components/church_center/church_center_icon"
import NewAttendeeListItem from "components/church_center/attendee_selection/new_attendee_list_item"
import { gendersMatch } from "utils/genders_match"

const AttendeeSelectionView = ({
  canRegisterHouseholdMembers,
  canRegisterOutsideMembers,
  attendeeSelections,
  attendeeTypes,
  attendees,
  currentPersonVerified,
  disabledSubmit,
  eventCapacityRemaining,
  eventStartDate,
  genderOptions,
  gradeOptions,
  guest,
  households,
  loginUrl,
  mobileBrowser,
  newAttendees,
  newAttendeesList,
  onAttendeeSelectionChange,
  onNewAttendee,
  onNewAttendeeChange,
  onSubmit,
  currentRegistrationUrl
}) => {
  const hasTypes = attendees => {
    return attendees.filter(a => a.attendeeTypeId !== "").length === attendees.length
  }

  const getAttendeeType = id => {
    return attendeeTypes.find(type => type.id === id)
  }

  const checkAge = attendee => {
    const attendeeType = getAttendeeType(attendee.attendeeTypeId)
    const dateToCheckAgeAgainst = attendeeType.evaluateAgeAt
      ? attendeeType.evaluateAgeAt
      : eventStartDate
    if (!attendeeType.hasAgeRestrictions || attendee === false) {
      return true
    }

    return meetsAttendeeTypeAgeRestriction(attendeeType, attendee.birthdate, dateToCheckAgeAgainst)
  }

  const checkGrade = attendee => {
    const attendeeType = getAttendeeType(attendee.attendeeTypeId)
    let { minGrade, maxGrade } = attendeeType

    if (!attendeeType.hasGradeRestrictions) {
      return true
    }

    maxGrade = maxGrade ?? 12
    minGrade = minGrade ?? -1

    return attendee.grade !== "" && minGrade <= attendee.grade && attendee.grade <= maxGrade
  }

  const checkGender = attendee => {
    const attendeeType = getAttendeeType(attendee.attendeeTypeId)
    if (!attendeeType.hasGenderRestrictions) {
      return true
    }
    return gendersMatch(attendeeType.genderRestrictionUnit, attendee.gender)
  }

  const allAttendeesFitFilters = () => {
    return [...newAttendees, ...attendees].every(attendee => {
      return (
        attendee != null &&
        attendee.attendeeTypeId != null &&
        checkAge(attendee) &&
        checkGrade(attendee) &&
        checkGender(attendee)
      )
    })
  }

  const newAttendeesHaveNames = () => {
    return (
      newAttendees.filter(a => a.firstName.trim().length > 0 && a.lastName.trim().length > 0)
        .length === newAttendees.length
    )
  }

  const allAttendeesHaveTypes = () => {
    return (
      hasTypes(attendees) &&
      hasTypes(newAttendees) &&
      (attendees.length > 0 || newAttendees.length > 0)
    )
  }

  const withinCapacity = () => {
    return withinTypeCapacities() && withinEventCapacity()
  }

  const withinTypeCapacities = () => {
    const selected = {}
    attendees.concat(newAttendees).forEach(a => {
      if (selected[a.attendeeTypeId]) {
        selected[a.attendeeTypeId] += 1
      } else {
        selected[a.attendeeTypeId] = 1
      }
    })

    return (
      attendeeTypes.filter(a => withinTypeCapacity(a, selected[a.id])).length ===
      attendeeTypes.length
    )
  }

  const withinTypeCapacity = ({ quantity }, count) => {
    if (quantity !== null && count !== undefined) {
      return quantity >= count
    } else {
      return true
    }
  }

  const withinEventCapacity = () => {
    if (eventCapacityRemaining !== null) {
      return attendees.concat(newAttendees).length <= eventCapacityRemaining
    } else {
      return true
    }
  }

  const registrationReady = () => {
    return (
      allAttendeesHaveTypes() &&
      newAttendeesHaveNames() &&
      withinCapacity() &&
      allAttendeesFitFilters()
    )
  }

  const registrationErrorMessage = () => {
    let message
    if (registrationReady()) {
      return null
    } else if (!allAttendeesHaveTypes()) {
      // noop
    } else if (!allAttendeesFitFilters()) {
      message = "Some attendees do not meet selection requirements."
    } else if (!newAttendeesHaveNames()) {
      message = "First and last name are required."
    } else if (!withinEventCapacity()) {
      message = "Signup is over capacity."
    } else if (!withinTypeCapacities()) {
      message = "Selections are over capacity."
    } else {
      message = "Error!"
    }

    if (message) {
      return <div className="error-alert alert mb-3">{message}</div>
    } else {
      return null
    }
  }

  return (
    <div>
      <div>
        <div>
          <h1 className="h3 mb-2">Select participants</h1>
          {guest && canRegisterHouseholdMembers && (
            <div className="info-alert alert mb-2">
              When registering as a guest, you can only register yourself. If you want to register a
              friend or family member, please <a href={loginUrl}>log in</a>.
            </div>
          )}
          {attendeeSelections.map((attendeeSelection, i) => {
            const attendee = attendees.filter(
              attendee => attendee.personId === attendeeSelection.personId
            )[0]

            return (
              <ExistingAttendeeListItem
                gradeOptions={gradeOptions}
                genderOptions={genderOptions}
                attendeeSelection={{ ...attendeeSelection, ...attendee }}
                attendeeTypes={attendeeTypes}
                eventStartDate={eventStartDate}
                onChange={onAttendeeSelectionChange}
                checked={guest || !!attendee}
                key={attendeeSelection.key}
                zValue={9000 - i}
                mobileBrowser={mobileBrowser}
                currentPersonVerified={currentPersonVerified}
                currentRegistrationUrl={currentRegistrationUrl}
              />
            )
          })}
          {(canRegisterHouseholdMembers || canRegisterOutsideMembers) && (
            <div>
              <div>
                {newAttendeesList.map(na => (
                  <NewAttendeeListItem
                    attendeeTypes={attendeeTypes}
                    households={households}
                    addMoreThenOneAttendee={na.addMoreThenOneAttendee}
                    currentPersonVerified={currentPersonVerified}
                    onChange={onNewAttendeeChange}
                    key={na.key}
                    gradeOptions={gradeOptions}
                    genderOptions={genderOptions}
                    zValue={na.zValue}
                    eventStartDate={eventStartDate}
                  />
                ))}
                <div className="ta-c ta-r@md">
                  {!guest && (
                    <button className="minor-btn secondary-btn btn" onClick={onNewAttendee}>
                      <div style={{ fontSize: 9, marginRight: 4 }}>
                        <Icon symbol="general#plus" aria-hidden />
                      </div>
                      Add someone else
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="pt-3">
        {registrationErrorMessage()}
        <div className="ta-c mb-2">
          <button
            disabled={!registrationReady() || disabledSubmit}
            className={`btn ${allAttendeesHaveTypes() ? "btn" : null}`}
            onClick={onSubmit}
          >
            Next: Details
          </button>
        </div>
      </div>
    </div>
  )
}

AttendeeSelectionView.propTypes = {
  canRegisterHouseholdMembers: bool,
  canRegisterOutsideMembers: bool
}

export default AttendeeSelectionView
